/**
 * injectTypingSpeed - Inyecta configuración de velocidad de escritura para el efecto typewriter
 *
 * Este módulo permite ajustar la velocidad de escritura del asistente según la configuración
 * de typing-speed: slow, default, fast
 *
 * @module injectTypingSpeed
 */

/**
 * Configuración de velocidades disponibles
 */
interface TypingSpeedConfig {
  tokenDelay: number;
  charVariance: number;
  minDelay: number;
}

/**
 * Mapeo de velocidades con sus configuraciones
 * - slow: 100% más lento que default
 * - default: velocidad rápida (antigua fast)
 * - fast: velocidad muy rápida (60% más rápido que antigua default)
 */
const SPEED_CONFIG: Record<string, TypingSpeedConfig> = {
  'slow': {
    tokenDelay: 3.5,   // Velocidad lenta (anteriormente 5ms)
    charVariance: 5,   // Varianza proporcional
    minDelay: 10       // Delay mínimo proporcional
  },
  'default': {
    tokenDelay: 2.5,   // Velocidad rápida (30% más rápido: 3.5ms * 0.7)
    charVariance: 3.5, // Varianza proporcional (5ms * 0.7)
    minDelay: 7        // Delay mínimo proporcional (10ms * 0.7)
  },
  'fast': {
    tokenDelay: 1.4,   // Velocidad muy rápida (30% más rápido: 2ms * 0.7)
    charVariance: 2,   // Varianza proporcional (3ms * 0.7)
    minDelay: 4        // Delay mínimo proporcional (6ms * 0.7)
  }
};

/**
 * Inyecta la configuración de velocidad de escritura en el DOM
 *
 * Lee la configuración de typing-speed desde chat-colors y aplica las constantes
 * correspondientes como CSS variables para que el efecto typewriter pueda usarlas
 *
 * @example
 * // En el loader, después de cargar la configuración:
 * injectTypingSpeed();
 *
 * @returns {void}
 */
export function injectTypingSpeed(): void {
  try {
    // Obtener la configuración de typing-speed desde chat-colors
    const config      = ( window as any ).HELLOMATIK_CONFIG || {};
    const chatColors  = config['chat-colors'] || {};
    const typingSpeed = chatColors['typing-speed'] || 'default';

    // Obtener la configuración según la velocidad seleccionada
    const speedConfig = SPEED_CONFIG[typingSpeed] || SPEED_CONFIG['default'];

    // Crear elemento <style> para las variables CSS
    const styleElement = document.createElement( 'style' );
    styleElement.id = 'hellomatik-typing-speed';

    // Construir el CSS con las variables aplicadas solo a #hm-popup
    let cssRules = '/* Hellomatik - Typing speed for #hm-popup */\n#hm-popup {\n';
    cssRules += `  --typewriter-token-delay: ${speedConfig.tokenDelay};\n`;
    cssRules += `  --typewriter-char-variance: ${speedConfig.charVariance};\n`;
    cssRules += `  --typewriter-min-delay: ${speedConfig.minDelay};\n`;
    cssRules += '}\n';

    styleElement.textContent = cssRules;

    // Insertar en el <head> del documento
    document.head.appendChild( styleElement );

  } catch( error ) {
    if( import.meta.env.DEV ) console.error( '[Typing Speed] Failed to inject typing speed:', error );
  }
}

/**
 * Resetea la configuración de velocidad de escritura a los valores por defecto
 * Útil para pruebas o cuando se quiere volver a la configuración original
 *
 * @returns {void}
 */
export function resetTypingSpeed(): void {
  const styleElement = document.getElementById( 'hellomatik-typing-speed' );

  if( styleElement )
    styleElement.remove();
}
